import { useState } from "react";
import { Modal, Form, Select, Upload, Button, message } from "antd";
import { InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { useImageUpload } from "../_actions/bot";
import log from "cslog";

const { Dragger } = Upload;

// Sample categories - replace with your actual categories
const CATEGORIES = [
    { id: "fashion_and_beauty", name: "Fahsion and Beauty" },
    { id: "healthcare", name: "Healthcare" },
    { id: "technology", name: "Technology" },
    { id: "people", name: "People" },
    { id: "food", name: "Food" },
];

// Sample subcategories - replace with your actual subcategories
const SUBCATEGORIES = {
    fashion_and_beauty: [
        { id: "hair", name: "Hair" },
        { id: "nails", name: "Nails" },
        { id: "plants", name: "Plants" },
    ],
    healthcare: [
        { id: "medicine", name: "medicine" },
        { id: "heart", name: "heart" },
        { id: "corporate", name: "Corporate" },
    ],
    technology: [
        { id: "computers", name: "Computers" },
        { id: "mobile", name: "Mobile" },
        { id: "software", name: "Software" },
    ],
    people: [
        { id: "portraits", name: "Portraits" },
        { id: "groups", name: "Groups" },
        { id: "lifestyle", name: "Lifestyle" },
    ],
    food: [
        { id: "cuisine", name: "Cuisine" },
        { id: "desserts", name: "Desserts" },
        { id: "beverages", name: "Beverages" },
    ],
};

export default function UploadingImageModal({ trigger }) {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const imageUploadMutation = useImageUpload((result) => {
        log.d(result, "result in useImageUpload");
        if (result?.success) {
            message.success("Image uploaded successfully");
        }
    });

    const handleCategoryChange = (value) => {
        setSelectedCategory(value);
        // Reset subcategory when category changes
        form.setFieldValue("subcategory", undefined);
    };

    const uploadProps = {
        name: "file",
        multiple: true,
        fileList: fileList,
        beforeUpload: (file) => {
            const isImage = file.type.startsWith("image/");
            if (!isImage) {
                message.error(`${file.name} is not an image file`);
                return false;
            }
            setFileList((prev) => [...prev, file]);
            return false; // Prevent automatic upload
        },
        onRemove: (file) => {
            setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
        },
        onChange: (info) => {
            log.d(info, "Upload onChange");
        },
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // Get base64 string without the data:image/xxx;base64, prefix
                const base64String = reader.result.split(",")[1];
                resolve(base64String);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleSubmit = async (values) => {
        if (fileList.length === 0) {
            message.error("Please select at least one image");
            return;
        }

        log.d(values, "Form values");
        log.d(fileList, "Files to upload");

        setUploading(true);

        try {
            // Convert all images to base64
            const base64Images = await Promise.all(
                fileList.map((file) => convertToBase64(file))
            );

            log.d(base64Images, "Base64 images");

            imageUploadMutation.mutate({
                category: values.category,
                sub_category: values.subcategory,
                images: base64Images,
            });

            setTimeout(() => {
                message.success(
                    `${fileList.length} image(s) uploaded successfully`
                );
                setUploading(false);
                setFileList([]);
                form.resetFields();
                setSelectedCategory(null);
                setOpen(false);
            }, 2000);
        } catch (error) {
            log.e("Error converting images to base64", error);
            message.error("Failed to process images");
            setUploading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        setSelectedCategory(null);
        setOpen(false);
    };

    return (
        <>
            <span onClick={() => setOpen(true)}>{trigger}</span>

            <Modal
                title="Image Upload"
                open={open}
                onCancel={handleCancel}
                footer={null}
                maskClosable={false}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: "20px" }}
                >
                    <Form.Item
                        name="category"
                        label="Category"
                        rules={[
                            {
                                required: true,
                                message: "Please select a category",
                            },
                        ]}
                    >
                        <Select
                            placeholder="Select a category"
                            onChange={handleCategoryChange}
                            options={CATEGORIES.map((cat) => ({
                                label: cat.name,
                                value: cat.id,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="subcategory"
                        label="Sub Category"
                        rules={[
                            {
                                required: true,
                                message: "Please select a subcategory",
                            },
                        ]}
                    >
                        <Select
                            placeholder="Select a subcategory"
                            disabled={!selectedCategory}
                            options={
                                selectedCategory
                                    ? SUBCATEGORIES[selectedCategory]?.map(
                                          (subcat) => ({
                                              label: subcat.name,
                                              value: subcat.id,
                                          })
                                      )
                                    : []
                            }
                        />
                    </Form.Item>

                    <Form.Item label="Upload Images">
                        <Dragger {...uploadProps}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag file to this area to upload
                            </p>
                            <p className="ant-upload-hint">
                                Support for single or bulk upload. Select
                                multiple images at once.
                            </p>
                        </Dragger>
                    </Form.Item>

                    <Form.Item>
                        <div
                            style={{
                                display: "flex",
                                gap: "10px",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={uploading}
                                icon={<UploadOutlined />}
                            >
                                Upload{" "}
                                {fileList.length > 0 && `(${fileList.length})`}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
